import { VERSION } from '../version.js';

/**
 * Noticing that a newer build has been deployed.
 *
 * There is no service worker, so a fresh load always gets the newest page.
 * The trouble is that an installed app is rarely loaded fresh: Android keeps
 * it alive in the background, and switching back to it resumes the page that
 * was already open — old code and all — until the app is killed. A player
 * found themselves force-closing the app to see each update.
 *
 * So the page asks. Every build ships a small `version.json` beside itself
 * (see vite.config.ts); on coming back to the foreground, on regaining a
 * connection, and every ten minutes while open, the page fetches it past every
 * cache and compares build numbers. What happens next is the caller's call:
 * reload at once somewhere nothing can be lost, or offer it and wait.
 */

const EVERY_MS = 10 * 60_000;
/** Refuse to reload twice for the same build: a stale edge copy must not loop. */
const RELOADED_FOR = 'nt2.reloadedFor';

export interface UpdateWatcher {
  /** The newer build's label, once one has been seen. */
  readonly ready: string | null;
  /**
   * Reload now if a newer build is waiting. Returns whether it did. `force`
   * is for a player pressing the button, who gets a reload whatever happened
   * last time.
   */
  applyIfReady(force?: boolean): boolean;
  /** Check straight away. */
  check(): Promise<void>;
}

export function watchForUpdates(opts: {
  /** True where a reload would lose nothing: the front page. */
  safeToReload(): boolean;
  /** A newer build is waiting and it was not safe to reload. Called once. */
  onReady(label: string): void;
}): UpdateWatcher {
  let ready: string | null = null;
  let readyBuild = 0;
  let told = false;
  let inFlight = false;

  const reload = (force = false): boolean => {
    try {
      if (!force && sessionStorage.getItem(RELOADED_FOR) === String(readyBuild)) return false;
      sessionStorage.setItem(RELOADED_FOR, String(readyBuild));
    } catch { /* no session storage: reload anyway, the guard is a nicety */ }
    location.reload();
    return true;
  };

  const check = async (): Promise<void> => {
    // A development server has no build number and no version file.
    if (!VERSION.build || inFlight || document.visibilityState === 'hidden') return;
    inFlight = true;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const v = await res.json() as { build?: unknown; label?: unknown };
      const build = Number(v.build);
      if (!Number.isInteger(build) || build <= VERSION.build) return;
      ready = typeof v.label === 'string' ? v.label : String(build);
      readyBuild = build;
      if (opts.safeToReload() && reload()) return;
      if (!told) { told = true; opts.onReady(ready); }
    } catch {
      /* offline, or the file is missing: try again next time */
    } finally {
      inFlight = false;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.addEventListener('pageshow', () => void check());
  window.addEventListener('online', () => void check());
  window.setInterval(() => void check(), EVERY_MS);
  window.setTimeout(() => void check(), 4000);

  return {
    get ready() { return ready; },
    applyIfReady: (force = false) => (ready ? reload(force) : false),
    check,
  };
}
